/** Pure transform math for CanvasHiResImageViewer.
 *
 * Scale semantics: scale === 1 means 1 image pixel = 1 CSS pixel (Actual Size).
 * Fit-to-view uses fitScale = min(vw/nw, vh/nh). Transform origin is top-left
 * of the image (0,0); tx/ty place the image in viewport coordinates.
 */

export type HiResTransform = {
  scale: number
  tx: number
  ty: number
}

export type Size = { w: number; h: number }

/** 800% actual-pixel inspection. */
export const MAX_ACTUAL_SCALE = 8

/** Soft floor relative to CSS pixels — never force above fitScale for huge images. */
export const SOFT_MIN_ACTUAL_SCALE = 0.1

export function computeFitScale(image: Size, viewport: Size): number {
  if (image.w <= 0 || image.h <= 0 || viewport.w <= 0 || viewport.h <= 0) return 1
  return Math.min(viewport.w / image.w, viewport.h / image.h)
}

export function computeMinScale(fitScale: number): number {
  if (!Number.isFinite(fitScale) || fitScale <= 0) return SOFT_MIN_ACTUAL_SCALE
  return Math.min(fitScale, SOFT_MIN_ACTUAL_SCALE)
}

export function clampScale(scale: number, fitScale: number): number {
  const min = computeMinScale(fitScale)
  return Math.min(MAX_ACTUAL_SCALE, Math.max(min, scale))
}

/** Center the image in the viewport at the given scale. */
export function fitTransform(image: Size, viewport: Size, scale = computeFitScale(image, viewport)): HiResTransform {
  const s = clampScale(scale, computeFitScale(image, viewport))
  return {
    scale: s,
    tx: (viewport.w - image.w * s) / 2,
    ty: (viewport.h - image.h * s) / 2,
  }
}

/** Actual-size (100%) transform centered on a viewport point (image-local focus). */
export function actualSizeTransform(
  image: Size,
  viewport: Size,
  focusCssX: number,
  focusCssY: number,
  prev: HiResTransform,
): HiResTransform {
  const fit = computeFitScale(image, viewport)
  const nextScale = clampScale(1, fit)
  return zoomAtPoint(prev, nextScale, focusCssX, focusCssY, image, viewport)
}

/**
 * Zoom so the image point currently under (px, py) stays under that cursor.
 * (px, py) are viewport-local CSS coordinates.
 */
export function zoomAtPoint(
  prev: HiResTransform,
  nextScaleRaw: number,
  px: number,
  py: number,
  image: Size,
  viewport: Size,
): HiResTransform {
  const fit = computeFitScale(image, viewport)
  const nextScale = clampScale(nextScaleRaw, fit)
  if (nextScale === prev.scale) {
    return clampPan(prev, image, viewport)
  }
  const imgX = (px - prev.tx) / prev.scale
  const imgY = (py - prev.ty) / prev.scale
  const next: HiResTransform = {
    scale: nextScale,
    tx: px - imgX * nextScale,
    ty: py - imgY * nextScale,
  }
  return clampPan(next, image, viewport)
}

/** Keep the image inspectable: smaller than viewport → centered; larger → all edges reachable. */
export function clampPan(t: HiResTransform, image: Size, viewport: Size): HiResTransform {
  const iw = image.w * t.scale
  const ih = image.h * t.scale
  let { tx, ty } = t

  if (iw <= viewport.w) {
    tx = (viewport.w - iw) / 2
  } else {
    tx = Math.min(0, Math.max(viewport.w - iw, tx))
  }

  if (ih <= viewport.h) {
    ty = (viewport.h - ih) / 2
  } else {
    ty = Math.min(0, Math.max(viewport.h - ih, ty))
  }

  return { scale: t.scale, tx, ty }
}

/**
 * When swapping preview → master (different natural pixels), keep the same
 * visual inspection point by converting through normalized image coords.
 */
export function remapTransformForImageSize(
  prev: HiResTransform,
  prevImage: Size,
  nextImage: Size,
  viewport: Size,
): HiResTransform {
  if (prevImage.w <= 0 || prevImage.h <= 0 || nextImage.w <= 0 || nextImage.h <= 0) {
    return fitTransform(nextImage, viewport)
  }
  const focusNormX = (viewport.w / 2 - prev.tx) / (prev.scale * prevImage.w)
  const focusNormY = (viewport.h / 2 - prev.ty) / (prev.scale * prevImage.h)
  // Preserve CSS size of the full frame: scale_new * nextW ≈ scale_old * prevW
  const visualWidth = prev.scale * prevImage.w
  const nextScale = clampScale(visualWidth / nextImage.w, computeFitScale(nextImage, viewport))
  const next: HiResTransform = {
    scale: nextScale,
    tx: viewport.w / 2 - focusNormX * nextImage.w * nextScale,
    ty: viewport.h / 2 - focusNormY * nextImage.h * nextScale,
  }
  return clampPan(next, nextImage, viewport)
}

export function panBy(prev: HiResTransform, dx: number, dy: number, image: Size, viewport: Size): HiResTransform {
  return clampPan({ scale: prev.scale, tx: prev.tx + dx, ty: prev.ty + dy }, image, viewport)
}

export function isNearFit(scale: number, fitScale: number, epsilon = 0.02): boolean {
  if (fitScale <= 0) return true
  return Math.abs(scale - fitScale) / fitScale <= epsilon
}

export function isNearActual(scale: number, epsilon = 0.02): boolean {
  return Math.abs(scale - 1) <= epsilon
}

export function formatZoomPercent(scale: number): string {
  return `${Math.round(scale * 100)}%`
}
